import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Download, ArrowRight } from "lucide-react";
import { API } from "../../lib/api";
import { toast } from "sonner";

export default function CustomerPortfolio() {
  const [lots, setLots] = useState([]);
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    api.get("/customer/my-lots").then((r) => setLots(r.data));
    api.get("/customer/purchases").then((r) => setPurchases(r.data));
  }, []);

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("gf_token");
      const r = await fetch(`${API}/reports/customer/portfolio`, { headers: { Authorization: `Bearer ${token}` }});
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "my_portfolio.xlsx";
      a.click(); URL.revokeObjectURL(url);
    } catch (_) { toast.error("Failed to download"); }
  };

  return (
    <div>
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl mb-8 p-8 md:p-10 text-white"
        style={{ background: "linear-gradient(135deg, #1F1816 0%, #7A1010 60%, #D71921 100%)" }}>
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F2C97A]/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-[#3F6B3F]/20 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-xs font-bold uppercase tracking-widest mb-3">
              My investments
            </div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tight">My portfolio</h1>
            <p className="text-white/80 mt-2 max-w-xl">All your active and completed lot investments, in one view.</p>
          </div>
          <button onClick={downloadReport} data-testid="download-portfolio-xlsx"
            className="px-5 py-2.5 rounded-xl bg-white/15 backdrop-blur border border-white/25 hover:bg-white/25 text-sm flex items-center gap-2 transition-all">
            <Download className="w-4 h-4" /> Excel Report
          </button>
        </div>
      </div>

      {lots.length === 0 ? (
        <div className="gf-card p-10 text-center">
          <div className="font-display text-xl">You have no lots yet</div>
          <Link to="/lots" className="inline-block mt-3 px-5 py-2.5 rounded-xl text-white text-sm font-medium shadow-lg shadow-[#D71921]/20"
            style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>Browse lots</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {lots.map((l, i) => {
            const statusColors = {
              in_progress: { bg: "linear-gradient(135deg, #3F6B3F 0%, #2A4A2A 100%)", text: "white" },
              ready_to_sell: { bg: "linear-gradient(135deg, #B8862A 0%, #8E6517 100%)", text: "white" },
              sold_out: { bg: "linear-gradient(135deg, #1F1816 0%, #7A1010 100%)", text: "white" },
            };
            const sc = statusColors[l.status] || { bg: "linear-gradient(135deg, #6B5F58 0%, #1F1816 100%)", text: "white" };
            return (
              <Link key={l.id} to={`/portfolio/${l.id}`} data-testid={`portfolio-${l.lot_code}`}
                className="rounded-2xl border border-[#E8E0D2] bg-white overflow-hidden hover:shadow-xl hover:shadow-[#D71921]/10 hover:-translate-y-1 transition-all">
                <div className="relative p-5 text-white" style={{ background: sc.bg }}>
                  <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
                  <div className="relative flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest opacity-80">{l.lot_code}</div>
                      <div className="font-display text-xl">{l.name}</div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 backdrop-blur border border-white/25">
                      {l.status.replace("_", " ")}
                    </span>
                  </div>
                </div>
                <div className="p-5 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-[10px] uppercase text-[#6B5F58]">Investment</div>
                    <div className="font-display text-lg text-[#D71921]">₹{l.price_per_lot.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#6B5F58]">Animals</div>
                    <div className="font-display text-lg text-[#3F6B3F]">{l.animal_count}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#6B5F58]">{l.status === "sold_out" ? "Profit" : "Expenses"}</div>
                    <div className="font-display text-lg text-[#B8862A]">₹{(l.status === "sold_out" ? (l.profit_to_customer || 0) : (l.total_expenses || 0)).toLocaleString()}</div>
                  </div>
                </div>
                <div className="px-5 pb-4 text-right text-[#D71921] text-xs font-semibold flex items-center justify-end gap-1">
                  Open <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {purchases.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-2xl mb-4">Recent transactions</h2>
          <div className="gf-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F4E9] text-xs uppercase tracking-wider text-[#6B5F58]">
                <tr>
                  <th className="text-left px-4 py-3">Invoice</th>
                  <th className="text-left px-4 py-3">Lot</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-t border-[#E8E0D2]">
                    <td className="px-4 py-3 font-mono text-xs">{p.invoice_number || "—"}</td>
                    <td className="px-4 py-3">{p.lot?.lot_code} · {p.lot?.name}</td>
                    <td className="px-4 py-3 text-right">₹{p.amount.toLocaleString()}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${
                      p.status === "paid" ? "bg-[#FCEDED] text-[#D71921]" : "bg-amber-100 text-amber-800"}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
